// axios.ts
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  AxiosRequestConfig,
} from "axios";
import store, { RootState } from "../store";
import { setTokensAction, clearTokensAction } from "../store/auth/authState";

const baseURL = process.env.REACT_APP_API_URL as string;

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const state: RootState = store.getState();
    const accessToken = state.auth.accessToken;
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const originalRequest = config;

    if (response && response.status === 401) {
      const state: RootState = store.getState();
      const refreshToken = state.auth.refreshToken;

      if (!refreshToken) {
        store.dispatch(clearTokensAction());
        window.location.href = "/auth/sign-in";
        return Promise.reject(error);
      }

      // Offline: the auth server is unreachable, so a refresh can't succeed —
      // and clearing tokens would sign the admin out over a network blip.
      // Keep the session; the next online request refreshes normally.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        isRefreshing = true;

        try {
          // Refresh with the RAW axios export, not axiosInstance: routing the
          // refresh call through this interceptor would queue it behind the
          // very refresh it's part of (isRefreshing is true) and deadlock
          // when the refresh token is rejected.
          const response = await axios.post(`${baseURL}/auth/refresh-token`, {
            refreshToken,
          });
          const newAccessToken = response.data.accessToken;
          // Token rotation on refresh: persist BOTH the new access token and
          // the rotated refresh token so a leaked refresh token has a short
          // useful life. Fall back to the existing refresh token if the
          // backend did not rotate it.
          const newRefreshToken = response.data.refreshToken ?? refreshToken;

          store.dispatch(
            setTokensAction({
              accessToken: newAccessToken,
              refreshToken: newRefreshToken,
            }),
          );
          processQueue(null, newAccessToken);

          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
          return axiosInstance(originalRequest);
        } catch (err) {
          processQueue(err, null);
          // Only end the session when the refresh endpoint genuinely
          // REJECTED the refresh token (401/403). A network failure or 5xx
          // must NOT log the admin out — the refresh token is likely still
          // valid, so keep the session and let a later request refresh.
          const status = (err as AxiosError)?.response?.status;
          if (status === 401 || status === 403) {
            store.dispatch(clearTokensAction());
            window.location.href = "/auth/sign-in";
          }
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      } else {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["Authorization"] = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
export type { AxiosResponse, AxiosRequestConfig };
